import React, { ReactNode, RefObject } from 'react';
import { AgGridReact } from 'ag-grid-react';
import SearchInput from './SearchInput';
import { BtnDefault } from './Buttons';
import { useThemeStore } from '../../state/theme';
import { RotateCcw, Filter } from 'lucide-react';
import { downloadCSV } from '../../utils';
import { COLORS, general } from '../../constants';

type FilterOption = {
  value: string;
  label: string;
  count?: number;
  color?: string;
};
type FilterGroup = {
  enabled: boolean;
  options: FilterOption[];
  selected: string | string[];
  onChange: (value: string) => void;
};

type Props = {
  tableRef?: RefObject<AgGridReact<any>>;
  title?: string;
  description?: string;
  filters?: {
    categories?: FilterGroup;
    assignments?: FilterGroup;
    status?: FilterGroup;
  };
  ctas?: {
    enableExportToCsv?: boolean;
    refresh?: { enabled: boolean; handler: () => void };
    custom?: ReactNode;
  };
  search?: string;
  setSearch?: (value: string) => void;
  isFilterOpen?: boolean;
  onToggleFilter?: () => void;
  activeFilterCount?: number;
};

const SearchBarCTAHeader: React.FC<Props> = ({
  tableRef,
  title,
  description,
  filters,
  ctas,
  search,
  setSearch,
  isFilterOpen,
  onToggleFilter,
  activeFilterCount = 0,
}) => {
  const { isDarkMode } = useThemeStore();

  const handleFilterChange = (value: string) => {
    setSearch?.(value);
  };

  const processCell = (params: any) => {
    const { value } = params;
    if (Array.isArray(value)) {
      return value
        .map((item) =>
          typeof item === 'object' ? item?.name || item?.value : item
        )
        .join(', ');
    }
    if (value instanceof Date) return value.toLocaleDateString();
    return typeof value === 'object'
      ? value?.name || value?.value || ''
      : value;
  };

  const exportJobApplications = async (api: any, fileName: string) => {
    const { getJobApplications } = await import('../../api/queries');

    const allData = await getJobApplications({
      startRow: 0,
      endRow: general.ROWS_TO_FETCH,
      filterModel: {},
      sortModel: [],
      search: '',
      category: '',
      status: '',
    });

    // Get metadata fields
    const metadataFields = new Set<string>();
    allData.rows.forEach((item: any) => {
      if (item.metadata && typeof item.metadata === 'object') {
        Object.keys(item.metadata).forEach((key) => metadataFields.add(key));
      }
    });

    // Create CSV manually
    const currentColumns = api.getColumnDefs() || [];
    const headers = currentColumns
      .filter((col: any) => col.headerName && col.field !== 'actions')
      .map((col: any) => col.headerName);

    // Add metadata headers
    metadataFields.forEach((field) => {
      headers.push(
        field
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      );
    });

    const csvRows = allData.rows.map((row: any) => {
      const values: string[] = [];

      // Get regular column values
      currentColumns.forEach((col: any) => {
        if (col.headerName && col.field !== 'actions') {
          let value = '';
          if (col.valueGetter) {
            value = col.valueGetter({ data: row });
          } else if (col.field) {
            value = col.field
              .split('.')
              .reduce((obj: any, key: string) => obj?.[key], row);
          }
          const processed = processCell({ value });
          values.push(
            processed ? `"${String(processed).replace(/"/g, '""')}"` : ''
          );
        }
      });

      // Add metadata values
      metadataFields.forEach((field) => {
        const value = row.metadata?.[field];
        const processed = Array.isArray(value) ? value.join(', ') : value || '';
        values.push(
          processed ? `"${String(processed).replace(/"/g, '""')}"` : ''
        );
      });

      return values.join(',');
    });

    const csvContent = [headers.join(','), ...csvRows].join('\n');
    downloadCSV(csvContent, fileName);
  };

  const exportAgents = async (api: any, fileName: string) => {
    const { getAgentList } = await import('../../api/queries');
    const allData = await getAgentList();

    if (allData.agents) {
      const currentColumns = api.getColumnDefs() || [];
      const headers = currentColumns
        .filter((col: any) => col.headerName && col.field !== 'actions')
        .map((col: any) => col.headerName);

      // Export ALL agents, not just the filtered ones shown in UI
      const csvRows = allData.agents.map((row: any) => {
        const values: string[] = [];
        currentColumns.forEach((col: any) => {
          if (col.headerName && col.field !== 'actions') {
            let value = '';
            if (col.valueGetter) {
              value = col.valueGetter({ data: row });
            } else if (col.field) {
              value = col.field
                .split('.')
                .reduce((obj: any, key: string) => obj?.[key], row);
            }
            const processed = processCell({ value });
            values.push(
              processed ? `"${String(processed).replace(/"/g, '""')}"` : ''
            );
          }
        });
        return values.join(',');
      });

      const csvContent = [headers.join(','), ...csvRows].join('\n');
      downloadCSV(csvContent, fileName);

      console.log(
        `Agent export completed: ${allData.agents.length} total agents exported`
      );
    }
  };

  const handleExport = async () => {
    const api = tableRef?.current?.api;
    if (!api) return;
    const fileName = `${title?.toLowerCase().replace(/\s+/g, '-') ?? 'export'}-${new Date().toISOString().split('T')[0]}.csv`;

    try {
      // For job applications with metadata
      if (title?.toLowerCase().includes('job applications')) {
        await exportJobApplications(api, fileName);
      }
      // For agents - fetch ALL agents from API, not just filtered ones
      // else if (title?.toLowerCase().includes('agent')) {
      //   await exportAgents(api, fileName);
      // }
      // Regular tables (including agents - respects filters)
      else {
        api.exportDataAsCsv({
          fileName,
          processCellCallback: processCell,
          allColumns: true,
        });
      }
    } catch (error) {
      console.error('Export failed:', error);
      // Fallback to default AG Grid export
      api.exportDataAsCsv({
        fileName,
        processCellCallback: processCell,
        allColumns: true,
      });
    }
  };

  const renderFilterGroup = (
    group?: FilterGroup,
    type?: 'status' | 'categories' | 'assignments'
  ) => {
    if (!group?.enabled) return null;
    // ${COLORS[value]}
    if (type === 'status') {
      const isSelected = (val: string) =>
        Array.isArray(group.selected)
          ? group.selected.includes(val)
          : group.selected === val;

      return (
        <div className="flex items-center gap-2 px-2 py-1 transition-colors flex-wrap">
          {group.options.map(({ value, label, count }) => (
            <div
              key={value}
              onClick={() => group.onChange(value)}
              className={`flex items-center px-3 py-1.5 cursor-pointer text-xs transition-colors rounded-full shadow-custom border-[1.5px] ${
                isSelected(value)
                  ? `text-blackFourty`
                  : ``
              }`}
              style={{
                borderColor: COLORS[value],
                backgroundColor: isSelected(value) ? `${COLORS[value]}80` : `${COLORS[value]}20`
              }}
            >
              {label}

              {count !== undefined && (
                <span
                  className={`ml-1 px-1 py-0 rounded-full text-xxs font-medium border border-greyFifty ${
                    isSelected(value)
                      ? 'bg-greyFourty bg-opacity-20 text-blackFourty'
                      : 'bg-greyThirty text-greyNinety'
                  }`}
                >
                  {count}
                </span>
              )}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-2 p-1 w-fit bg-greyTwenty rounded-10 border border-greyFifty">
        {group.options.map(({ value, label }) => (
          <div
            key={value}
            onClick={() => group.onChange(value)}
            className={`px-2 py-1 rounded-lg cursor-pointer text-sm transition-colors ${
              group.selected === value
                ? 'bg-white text-blackFourty shadow-custom font-medium'
                : 'text-greyHundred hover:text-black'
            }`}
          >
            {label}
          </div>
        ))}
      </div>
    );
  };

  const filtersEnabled =
    filters?.categories?.enabled ||
    filters?.assignments?.enabled ||
    filters?.status?.enabled;

  return (
    <div className="flex flex-col w-full gap-2 ml-auto flex-shrink-0">
      <div className="flex justify-between items-center">
        <div className="flex flex-col">
          <h1
            className={`text-xl ml-2 font-semibold mr-auto ${
              isDarkMode ? 'text-gray-100' : 'text-blackTen'
            }`}
          >
            {title}
          </h1>
          {description && (
            <p className="text-sm ml-2 text-greyNinety">{description}</p>
          )}
        </div>

        <div className="flex items-center justify-between flex-wrap gap-4">
          <SearchInput
            value={search || ''}
            onChange={setSearch || (() => {})}
            onClear={() => handleFilterChange('')}
            placeholder="Search across all columns..."
            width={filtersEnabled ? '300px' : '400px'}
          />

          {ctas && (
            <div className="flex gap-2 justify-end">
              {ctas.custom}
              {ctas.enableExportToCsv && (
                <BtnDefault onClick={handleExport} className="px-4 py-2">
                  Export to CSV
                </BtnDefault>
              )}
              {ctas.refresh?.enabled && (
                <BtnDefault
                  onClick={ctas.refresh.handler}
                  className="px-4 py-2"
                  icon={<RotateCcw size={16} />}
                >
                  Refresh
                </BtnDefault>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="w-full border-b border-greyFifty" />

      <div className="flex flex-col gap-3 mb-2.5">
        {filtersEnabled && (
          <div className="flex flex-col gap-3">
            <div className="flex gap-4 items-center justify-between">
              {renderFilterGroup(filters.categories, 'categories')}
              {renderFilterGroup(filters.assignments, 'assignments')}
              {onToggleFilter && (
                <button
                  onClick={onToggleFilter}
                  className={`relative p-2 rounded-lg border transition-colors ${
                    isFilterOpen
                      ? isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-blue-400'
                        : 'bg-blue-50 border-blue-200 text-blue-600'
                      : isDarkMode
                        ? 'border-gray-600 hover:bg-gray-700 text-gray-300'
                        : 'border-greyFifty hover:bg-greyTwenty text-greyNinety'
                  }`}
                  title={isFilterOpen ? 'Hide filters' : 'Show filters'}
                >
                  <div className='flex items-center gap-1'>
                    <Filter size={16} />
                    <span className='text-sm'>Filter</span>
                  </div>
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-blue-600 text-white text-xxs w-4 h-4 rounded-full flex items-center justify-center font-medium">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              )}
            </div>
            {filters?.status?.enabled && (
              <div className="flex">
                {renderFilterGroup(filters.status, 'status')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBarCTAHeader;
